<template>
  <!-- ═══════════ density: table — full register row (+ flag banner row) ═══════════ -->
  <template v-if="density === 'table'">
    <tr
      class="domain-row"
      :class="{ 'domain-row--flagged': isFlagged }"
      @click="$emit('click', domain)"
    >
      <td class="domain-row__chk-cell" @click.stop="$emit('check', domain)">
        <span class="domain-row__chk" :class="{ 'domain-row__chk--checked': checked }"></span>
      </td>
      <td>
        <div class="domain-row__dom-cell">
          <div class="domain-row__dom-ic"><GlobeIcon /></div>
          <div>
            <div class="domain-row__name-row">
              <span class="domain-row__name">{{ domain.domain }}</span>
              <span v-if="isFlagged" class="domain-row__flag"><WarnIcon />Flagged</span>
            </div>
            <div class="domain-row__sub">{{ subLine }}</div>
          </div>
        </div>
      </td>
      <td>
        <div class="domain-row__reg" :class="{ 'domain-row__reg--na': !domain.registrar }">{{ registrar }}</div>
        <div class="domain-row__source"><SourceIcon :via="domain.discovered_via" />{{ viaLabel }}</div>
      </td>
      <td :class="expiryToneClass">
        <template v-if="domain.expires_at">
          <div class="domain-row__expiry-val">{{ expiryDate }}</div>
          <div class="domain-row__expiry-rel"><WarnIcon v-if="tone" class="domain-row__expiry-warn-ic" />{{ expiryRel }}</div>
        </template>
        <span v-else class="domain-row__na">— <span v-if="domain.discovered_via === 'manual'" class="domain-row__na-note">(not supplied)</span></span>
      </td>
      <td>
        <div class="domain-row__autorenew">
          <span class="domain-row__toggle" :class="{ 'domain-row__toggle--off': !autoRenewOn }"></span>
          <span class="domain-row__ar-label">{{ autoRenewOn ? 'On' : 'Off' }}</span>
        </div>
      </td>
      <td>
        <DomainStateChip :state="domain.usage_state" />
        <div class="domain-row__state-source" :class="isManual ? 'domain-row__state-source--manual' : 'domain-row__state-source--derived'">
          <LockIcon v-if="isManual" />{{ stateCaption }}
        </div>
      </td>
      <td>
        <span v-if="domain.site_id" class="domain-row__site-link"><LinkIcon />Site #{{ domain.site_id }}</span>
        <span v-else class="domain-row__na">—</span>
      </td>
      <td><span class="domain-row__view">View &rarr;</span></td>
    </tr>
    <tr v-if="isFlagged" class="domain-row__banner-row">
      <td colspan="8">
        <div class="domain-row__banner">
          <WarnIcon />
          <span class="domain-row__banner-text">{{ domain.review_reason || 'A sync source stopped reporting this domain — could be a transfer-out or an API hiccup, they look identical from here.' }}</span>
          <span class="domain-row__banner-actions">
            <span @click.stop="$emit('resolve-review', domain)">Confirm &amp; resolve</span>
            <span @click.stop="$emit('recheck', domain)">Re-check now</span>
          </span>
        </div>
      </td>
    </tr>
  </template>

  <!-- ═══════════ density: picker — step-3a modal row ═══════════ -->
  <div
    v-else-if="density === 'picker'"
    class="domain-row domain-row--picker"
    :class="{ 'domain-row--picker-selected': selected }"
    @click="$emit('select', domain)"
  >
    <span class="domain-row__radio"></span>
    <div class="domain-row__picker-main">
      <div class="domain-row__picker-name-row">
        <span class="domain-row__name">{{ domain.domain }}</span>
        <DomainStateChip :state="domain.usage_state" size="sm" />
      </div>
      <div class="domain-row__picker-note">{{ pickerNote }}</div>
    </div>
    <div class="domain-row__picker-meta">
      <div class="domain-row__pm-expiry">{{ expiryDate || '—' }}</div>
      <div class="domain-row__pm-sub">{{ expiryRel || 'no expiry on file' }}</div>
    </div>
  </div>

  <!-- ═══════════ density: header — drawer header block ═══════════ -->
  <div v-else class="domain-row domain-row--header">
    <div class="domain-row__ph-icon"><GlobeIcon /></div>
    <div>
      <div class="domain-row__ph-title">
        <h3>{{ domain.domain }}</h3>
        <span class="domain-row__type-badge">Domain</span>
        <DomainStateChip :state="domain.usage_state" />
        <span v-if="isFlagged" class="domain-row__flag"><WarnIcon />Flagged</span>
      </div>
      <div class="domain-row__ph-sub">{{ headerSub }}</div>
    </div>
  </div>
</template>

<script>
// ═════════════════════════════════════════════════════════════════════════
// THE canonical Domain row — Plan 005 §4.6 register (`domains-register.html`).
//
// The approved design's hard requirement: the register's table row, the
// step-3a picker row and the detail drawer's header are visual VARIANTS OF
// THE SAME ROW DATA, not separate components — one component, three
// densities (`table` / `picker` / `header`), so chip colours, icons, state
// language and caption logic can never drift between the three surfaces.
// Do NOT fork this into per-surface row implementations.
//
// Shared design language encoded here (see the mockup's anatomy notes):
//  - "Unknown" renders as a dashed hollow pill (via DomainStateChip), never
//    a filled colour.
//  - A manual usage-state override renders the lock icon + "manual · <user>
//    · <date>" caption; a derived state renders quiet grey text with the
//    derivation reason.
//  - Flagged-for-review is a ROW treatment (left danger stripe + inline
//    banner row) plus a small "Flagged" tag — never a fifth state chip.
//  - Linked site renders only when a site is actually linked; everything
//    else is an explicit "—" (no invented data).
// ═════════════════════════════════════════════════════════════════════════
import { computed, defineComponent, h } from 'vue';
import DomainStateChip from '@root/components/Domains/DomainStateChip.vue';
import {
  derivationReason,
  derivedCaption,
  discoveredViaLabel,
  expiryRelLabel,
  expiryTone,
  formatDate,
  manualCaption,
  registrarLabel,
} from '@root/services/domainFormat';

// Tiny inline-SVG icon renderers (mockup's own paths) — kept as local
// functional components so all three densities share the exact same shapes.
const svg = (attrs, paths) => () => h(
  'svg',
  { viewBox: '0 0 18 18', fill: 'none', stroke: 'currentColor', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', ...attrs },
  paths.map(([tag, a]) => h(tag, a)),
);

const GlobeIcon = defineComponent({ render: svg({ 'stroke-width': '1.6' }, [
  ['circle', { cx: 9, cy: 9, r: 6.5 }],
  ['path', { d: 'M2.5 9h13' }],
  ['path', { d: 'M9 2.5c1.8 1.7 3 4 3 6.5s-1.2 4.8-3 6.5c-1.8-1.7-3-4-3-6.5s1.2-4.8 3-6.5Z' }],
]) });

const WarnIcon = defineComponent({ render: svg({ 'stroke-width': '1.8' }, [
  ['path', { d: 'M9 2.7 16 15.3H2Z' }],
  ['path', { d: 'M9 7.2v3.4' }],
  ['circle', { cx: 9, cy: 12.6, r: 0.3, fill: 'currentColor', stroke: 'none' }],
]) });

const LockIcon = defineComponent({ render: svg({ 'stroke-width': '1.6' }, [
  ['rect', { x: 4, y: 8, width: 10, height: 7, rx: 1.2 }],
  ['path', { d: 'M6 8V5.5a3 3 0 0 1 6 0V8' }],
]) });

const LinkIcon = defineComponent({ render: svg({ 'stroke-width': '1.6' }, [
  ['path', { d: 'M7 5H4.5A1.5 1.5 0 0 0 3 6.5v7A1.5 1.5 0 0 0 4.5 15h7a1.5 1.5 0 0 0 1.5-1.5V11' }],
  ['path', { d: 'M10 3h5v5' }],
  ['path', { d: 'M15 3l-7 7' }],
]) });

// Discovered-via badge icon: Enom = price tag, Cloudflare = shield,
// Manual = pencil, Pipeline = flag (mockup's set).
const SourceIcon = defineComponent({
  props: { via: { type: String, default: null } },
  setup(props) {
    const renderers = {
      enom: svg({ 'stroke-width': '1.6' }, [
        ['path', { d: 'M2.5 3h4.9l6.6 6.6-4.9 4.9L2.5 7.9Z' }],
        ['circle', { cx: 6, cy: 5.6, r: 0.7, fill: 'currentColor', stroke: 'none' }],
      ]),
      cloudflare: svg({ 'stroke-width': '1.6' }, [
        ['path', { d: 'M9 2.5 14.5 4.5v4.5c0 3.4-2.3 5.8-5.5 6.5-3.2-.7-5.5-3.1-5.5-6.5V4.5Z' }],
      ]),
      manual: svg({ 'stroke-width': '1.6' }, [
        ['path', { d: 'M3 15l.5-2.6L11.5 4.4a1.1 1.1 0 0 1 1.6 0l.5.5a1.1 1.1 0 0 1 0 1.6L5.6 14.5 3 15Z' }],
      ]),
      pipeline: svg({ 'stroke-width': '1.6' }, [
        ['path', { d: 'M4 15.5V3' }],
        ['path', { d: 'M4 3h9.5l-2.2 3 2.2 3H4' }],
      ]),
    };
    return renderers[props.via] || renderers.manual;
  },
});

export default {
  name: 'DomainRow',
  components: { DomainStateChip, GlobeIcon, WarnIcon, LockIcon, LinkIcon, SourceIcon },
  props: {
    domain: { type: Object, required: true },
    density: {
      type: String,
      default: 'table',
      validator: (v) => ['table', 'picker', 'header'].includes(v),
    },
    checked: { type: Boolean, default: false }, // table density
    selected: { type: Boolean, default: false }, // picker density
  },
  emits: ['click', 'check', 'select', 'resolve-review', 'recheck'],
  setup(props) {
    const isFlagged = computed(() => props.domain.review_flagged_at !== null && props.domain.review_flagged_at !== undefined);
    const isManual = computed(() => props.domain.usage_state_source === 'manual');
    const autoRenewOn = computed(() => !!Number(props.domain.auto_renew));
    const registrar = computed(() => registrarLabel(props.domain));
    const viaLabel = computed(() => discoveredViaLabel(props.domain.discovered_via));
    const expiryDate = computed(() => formatDate(props.domain.expires_at));
    const expiryRel = computed(() => expiryRelLabel(props.domain.expires_at));
    const tone = computed(() => expiryTone(props.domain.expires_at));
    const expiryToneClass = computed(() => (tone.value ? `domain-row__expiry--${tone.value}` : ''));
    const subLine = computed(() => derivationReason(props.domain));
    const stateCaption = computed(() => (isManual.value ? manualCaption(props.domain) : derivedCaption(props.domain)));
    const pickerNote = computed(() => props.domain.notes || 'no notes on file');
    const headerSub = computed(() => {
      const bits = [registrar.value];
      bits.push(props.domain.zone_ref ? `zone ${props.domain.zone_ref}` : 'no zone');
      bits.push(`auto-renew ${autoRenewOn.value ? 'on' : 'off'}`);
      return bits.join(' · ');
    });

    return {
      isFlagged,
      isManual,
      autoRenewOn,
      registrar,
      viaLabel,
      expiryDate,
      expiryRel,
      tone,
      expiryToneClass,
      subLine,
      stateCaption,
      pickerNote,
      headerSub,
    };
  },
};
</script>

<style lang="scss">
.domain-row {
  // ── table density ──────────────────────────────────────────────────────
  &--flagged {
    background: linear-gradient(90deg, rgba(209, 67, 67, .05), transparent 55%);

    td:first-child {
      box-shadow: inset 3px 0 0 var(--theme-status-danger);
    }
  }

  &__chk-cell {
    width: 24px;
  }

  &__chk {
    width: 14px;
    height: 14px;
    border-radius: 3px;
    border: 1.5px solid var(--theme-border-strong);
    position: relative;
    display: inline-block;
    cursor: pointer;

    &--checked {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);

      &::after {
        content: "";
        position: absolute;
        left: 3px;
        top: 1px;
        width: 4px;
        height: 7px;
        border: solid #fff;
        border-width: 0 1.6px 1.6px 0;
        transform: rotate(45deg);
      }
    }
  }

  &__dom-cell {
    display: flex;
    align-items: flex-start;
    gap: 9px;
  }

  &__dom-ic {
    width: 26px;
    height: 26px;
    border-radius: var(--theme-radius-sm, 3px);
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;

    svg {
      width: 13px;
      height: 13px;
    }
  }

  &__name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__name {
    font-family: var(--theme-font-mono);
    font-weight: 600;
    font-size: 12.5px;
    color: var(--theme-text-primary);
  }

  &__flag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .03em;
    padding: 2px 6px;
    border-radius: 3px;
    background: #fbe1e1;
    color: #a53232;
    margin-left: 7px;

    svg {
      width: 8px;
      height: 8px;
    }
  }

  &__sub {
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    margin-top: 3px;
    line-height: 1.5;
    max-width: 240px;
  }

  &__reg {
    font-size: 12.5px;
    color: var(--theme-text-primary);
    font-weight: 500;

    &--na {
      color: var(--theme-text-tertiary);
      font-weight: 400;
    }
  }

  &__source {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    margin-top: 3px;

    svg {
      width: 10px;
      height: 10px;
      flex-shrink: 0;
    }
  }

  &__expiry-val {
    font-family: var(--theme-font-mono);
    font-size: 12px;
    color: var(--theme-text-primary);
  }

  &__expiry-rel {
    font-size: 10.5px;
    margin-top: 2px;
    color: var(--theme-text-tertiary);
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  &__expiry-warn-ic {
    width: 10px;
    height: 10px;
  }

  &__expiry--warn {
    .domain-row__expiry-val,
    .domain-row__expiry-rel {
      color: #8a5a0d;
    }
  }

  &__expiry--danger {
    .domain-row__expiry-val,
    .domain-row__expiry-rel {
      color: #a53232;
    }

    .domain-row__expiry-val {
      font-weight: 700;
    }
  }

  &__na {
    color: var(--theme-text-tertiary);
  }

  &__na-note {
    font-size: 10px;
  }

  &__autorenew {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  &__toggle {
    width: 26px;
    height: 15px;
    border-radius: 8px;
    background: var(--theme-status-success);
    position: relative;
    flex-shrink: 0;
    display: inline-block;

    &::after {
      content: "";
      position: absolute;
      top: 1.5px;
      right: 1.5px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #fff;
    }

    &--off {
      background: var(--theme-border-strong);

      &::after {
        right: auto;
        left: 1.5px;
      }
    }
  }

  &__ar-label {
    font-size: 11px;
    color: var(--theme-text-secondary);
  }

  &__state-source {
    font-size: 10px;
    margin-top: 5px;
    display: flex;
    align-items: center;
    gap: 4px;

    &--derived {
      color: var(--theme-text-tertiary);
    }

    &--manual {
      color: var(--theme-accent-dark);
      font-weight: 700;
    }

    svg {
      width: 9px;
      height: 9px;
      flex-shrink: 0;
    }
  }

  &__site-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: var(--theme-accent-dark);
    font-weight: 600;

    svg {
      width: 11px;
      height: 11px;
      flex-shrink: 0;
    }
  }

  &__view {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    opacity: 0;
    white-space: nowrap;
  }

  &:hover &__view {
    opacity: 1;
  }

  &__banner-row td {
    padding: 0 !important;
  }

  &__banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px 8px 17px;
    font-size: 11.5px;
    color: #a53232;
    background: #fbe1e1;

    svg {
      width: 12px;
      height: 12px;
      flex-shrink: 0;
    }
  }

  &__banner-text {
    flex: 1;
    min-width: 0;
  }

  &__banner-actions {
    margin-left: auto;
    display: flex;
    gap: 14px;
    flex-shrink: 0;

    span {
      font-weight: 700;
      cursor: pointer;
      text-decoration: underline;
      white-space: nowrap;
    }
  }

  // ── picker density ─────────────────────────────────────────────────────
  &--picker {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 22px;
    border-bottom: 1px solid var(--theme-border-soft);
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }
  }

  &--picker-selected {
    background: var(--theme-accent-light);

    .domain-row__radio {
      border-color: var(--theme-accent-primary);

      &::after {
        content: "";
        position: absolute;
        inset: 3px;
        border-radius: 50%;
        background: var(--theme-accent-primary);
      }
    }
  }

  &__radio {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid var(--theme-border-strong);
    flex-shrink: 0;
    position: relative;
  }

  &__picker-main {
    flex: 1;
    min-width: 0;
  }

  &__picker-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__picker-note {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin-top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 340px;
  }

  &__picker-meta {
    text-align: right;
    flex-shrink: 0;
  }

  &__pm-expiry {
    font-size: 11px;
    color: var(--theme-text-secondary);
    font-family: var(--theme-font-mono);
  }

  &__pm-sub {
    font-size: 10px;
    color: var(--theme-text-tertiary);
    margin-top: 2px;
  }

  // ── header density ─────────────────────────────────────────────────────
  &--header {
    display: flex;
    gap: 14px;
    align-items: center;
  }

  &__ph-icon {
    width: 44px;
    height: 44px;
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-accent-light);
    color: var(--theme-accent-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  &__ph-title {
    display: flex;
    align-items: center;
    gap: 9px;
    flex-wrap: wrap;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      font-family: var(--theme-font-mono);
    }
  }

  &__type-badge {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);
    padding: 2px 7px;
    border-radius: var(--theme-radius-sm, 3px);
  }

  &__ph-sub {
    font-family: var(--theme-font-mono);
    font-size: 11.5px;
    color: var(--theme-text-tertiary);
    margin-top: 6px;
  }
}
</style>
